import { Component, Suspense, lazy, useEffect, type ErrorInfo, type ReactNode } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { LoadingScreen } from "./routes/LoadingScreen";
import { Toaster } from "./components/Toaster";
import { trackPageView } from "./analytics/analytics";
import { I18nProvider } from "./i18n";
import { isTeamMode } from "./productMode";
import {
  LANDING_PAGE_TITLE,
  ensureMeta,
  ensurePropertyMeta,
  syncBrowserTitle,
} from "./pageTitle";

const LandingPage = lazy(() =>
  import("./landing/LandingPage").then((module) => ({
    default: module.LandingPage,
  })),
);
const TeamLandingPage = lazy(() =>
  import("./landing/TeamLandingPage").then((module) => ({
    default: module.TeamLandingPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("./routes/ResetPasswordPage").then((module) => ({
    default: module.ResetPasswordPage,
  })),
);
const PrivateAppProviders = lazy(() =>
  import("./routes/PrivateAppProviders").then((module) => ({
    default: module.PrivateAppProviders,
  })),
);
const ScreenerRoute = lazy(() =>
  import("./routes/ScreenerRoute").then((module) => ({
    default: module.ScreenerRoute,
  })),
);
const AdminAuthWrapper = lazy(() =>
  import("./routes/admin/AdminAuthWrapper").then((module) => ({
    default: module.AdminAuthWrapper,
  })),
);
const AccountSessionGate = lazy(() =>
  import("./routes/AccountSessionGate").then((module) => ({
    default: module.AccountSessionGate,
  })),
);
const AdminSessionGate = lazy(() =>
  import("./routes/AdminSessionGate").then((module) => ({
    default: module.AdminSessionGate,
  })),
);
const OverviewTab = lazy(() =>
  import("./routes/admin/tabs/OverviewTab").then((module) => ({ default: module.OverviewTab })),
);
const DatabasesTab = lazy(() =>
  import("./routes/admin/tabs/DatabasesTab").then((module) => ({
    default: module.DatabasesTab,
  })),
);
const ConnectionsTab = lazy(() =>
  import("./routes/admin/tabs/ConnectionsTab").then((module) => ({
    default: module.ConnectionsTab,
  })),
);
const TwapMetricsTab = lazy(() =>
  import("./routes/admin/tabs/TwapMetricsTab").then((module) => ({
    default: module.TwapMetricsTab,
  })),
);
const LimitsTab = lazy(() =>
  import("./routes/admin/tabs/LimitsTab").then((module) => ({ default: module.LimitsTab })),
);
const ErrorsTab = lazy(() =>
  import("./routes/admin/tabs/ErrorsTab").then((module) => ({ default: module.ErrorsTab })),
);
const WorkersTab = lazy(() =>
  import("./routes/admin/tabs/WorkersTab").then((module) => ({ default: module.WorkersTab })),
);
const AdminDashboardTab = lazy(() =>
  import("./routes/admin/AdminDashboardTab").then((module) => ({
    default: module.AdminDashboardTab,
  })),
);
const AdminUsersTab = lazy(() =>
  import("./routes/admin/AdminUsersTab").then((module) => ({
    default: module.AdminUsersTab,
  })),
);
const AdminMonitoringTab = lazy(() =>
  import("./routes/admin/AdminMonitoringTab").then((module) => ({
    default: module.AdminMonitoringTab,
  })),
);
const AdminObservabilityTab = lazy(() =>
  import("./routes/admin/AdminObservabilityTab").then((module) => ({
    default: module.AdminObservabilityTab,
  })),
);

const SCREENER_PATH = "/screener";
const SITE_ORIGIN = "https://twap-detect.com";

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
    message,
  );
}

function chunkReloadKey(pathname: string, error: Error): string {
  const raw = `${error.name}:${error.message}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash * 31 + raw.charCodeAt(i)) >>> 0;
  }
  return `twap:chunk-reload:${pathname}:${hash.toString(16)}`;
}

class RouteErrorBoundary extends Component<
  { children: ReactNode; resetKey: string },
  { error: Error | null; chunkLoadFailed: boolean }
> {
  state: { error: Error | null; chunkLoadFailed: boolean } = {
    error: null,
    chunkLoadFailed: false,
  };

  static getDerivedStateFromError(error: Error) {
    return { error, chunkLoadFailed: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (!isChunkLoadError(error) || typeof window === "undefined") return;
    const key = chunkReloadKey(this.props.resetKey, error);
    if (window.sessionStorage.getItem(key) === "1") return;
    window.sessionStorage.setItem(key, "1");
    window.location.reload();
  }

  componentDidUpdate(prevProps: { resetKey: string }) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, chunkLoadFailed: false });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="route-error-root" role="alert">
        <div className="route-error-panel">
          <div className="route-error-title">
            {this.state.chunkLoadFailed ? "Интерфейс обновился" : "Экран не загрузился"}
          </div>
          <div className="route-error-text">
            {this.state.chunkLoadFailed
              ? "Нужно перезагрузить страницу, чтобы скачать свежие файлы."
              : "Можно перезагрузить страницу и открыть экран заново."}
          </div>
          <button type="button" className="route-error-button" onClick={() => window.location.reload()}>
            Перезагрузить
          </button>
        </div>
      </div>
    );
  }
}

export function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </I18nProvider>
  );
}

export function AppRoutes() {
  const location = useLocation();
  const PublicLandingPage = isTeamMode ? TeamLandingPage : LandingPage;

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  useEffect(() => {
    syncRouteSeo(location.pathname);
  }, [location.pathname]);

  return (
    <>
      <Toaster />
      <RouteErrorBoundary resetKey={location.pathname}>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route
              path="/admin"
              element={
                <PrivateAppProviders>
                  <AdminSessionGate>
                    <AdminAuthWrapper />
                  </AdminSessionGate>
                </PrivateAppProviders>
              }
            >
              <Route index element={<Navigate to="/admin/monitoring" replace />} />
              {/* E_ADMIN: 8 новых табов профессиональной админки */}
              <Route path="overview" element={<OverviewTab />} />
              <Route path="databases" element={<DatabasesTab />} />
              <Route path="connections" element={<ConnectionsTab />} />
              <Route path="twap-metrics" element={<TwapMetricsTab />} />
              <Route path="limits" element={<LimitsTab />} />
              <Route path="errors" element={<ErrorsTab />} />
              <Route path="workers" element={<WorkersTab />} />
              {/* Legacy табы */}
              <Route path="dashboard" element={<AdminDashboardTab />} />
              <Route path="users" element={<AdminUsersTab />} />
              <Route path="invites" element={<Navigate to="/admin/monitoring" replace />} />
              <Route path="monitoring" element={<AdminMonitoringTab />} />
              <Route path="observability" element={<AdminObservabilityTab />} />
              <Route path="*" element={<Navigate to="/admin/monitoring" replace />} />
            </Route>

            <Route path="/" element={<PublicLandingPage />} />
            <Route path="/ru" element={<PublicLandingPage />} />
            <Route path="/en" element={<PublicLandingPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path={SCREENER_PATH}
              element={
                <PrivateAppProviders>
                  <ScreenerRoute />
                </PrivateAppProviders>
              }
            />
            <Route path="/app" element={<Navigate to={SCREENER_PATH} replace />} />
            <Route path="/dashboard" element={<Navigate to={SCREENER_PATH} replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </>
  );
}

function syncRouteSeo(pathname: string) {
  const privateRoute = /^\/(screener|app|dashboard|admin|account|reset-password|go)(\/|$)/i.test(
    pathname,
  );
  const languagePath = pathname === "/en" ? "/en" : pathname === "/ru" ? "/ru" : "";
  const canonicalPath = languagePath;
  const canonicalUrl = `${SITE_ORIGIN}${canonicalPath || "/"}`;
  const previewImageUrl = `${SITE_ORIGIN}/td-logo-180.png`;
  const isEnglish = languagePath === "/en";
  const robots = ensureMeta("robots");
  robots.content = privateRoute || isTeamMode ? "noindex, nofollow" : "index, follow";
  syncBrowserTitle(LANDING_PAGE_TITLE);

  const canonical = ensureCanonical();
  canonical.href = canonicalUrl;
  document.documentElement.lang = isEnglish ? "en" : "ru";

  ensureHreflang("ru", `${SITE_ORIGIN}/ru`);
  ensureHreflang("en", `${SITE_ORIGIN}/en`);
  ensureHreflang("x-default", `${SITE_ORIGIN}/`);
  ensurePropertyMeta("og:url").content = canonicalUrl;
  ensurePropertyMeta("og:locale").content = isEnglish ? "en_US" : "ru_RU";
  ensurePropertyMeta("og:image").content = previewImageUrl;
  ensureMeta("twitter:image").content = previewImageUrl;
  ensurePropertyMeta("og:description").content =
    (isEnglish
      ? "Live Hyperliquid TWAP signals, whale order tracking, impact, strength and alerts."
      : "TWAP сигналы Hyperliquid, крупные ордера, кошельки, сила, влияние на цену и алерты.");
  ensureMeta("description").content =
    (isEnglish
      ? "Live crypto TWAP screener for Hyperliquid traders: whale orders, wallet statistics, price impact, strength, winrate and alerts."
      : "Крипто TWAP скринер для трейдеров Hyperliquid: крупные ордера, статистика кошельков, сила TWAP, влияние на цену, winrate и алерты.");
}

function ensureCanonical() {
  let element = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  return element;
}

function ensureHreflang(language: string, href: string) {
  let element = document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${language}"]`);
  if (!element) {
    element = document.createElement("link");
    element.rel = "alternate";
    element.hreflang = language;
    document.head.appendChild(element);
  }
  element.href = href;
  return element;
}
